export type WhatsAppConfiguration = {
  metaAppId: string
  graphApiVersion: string
  embeddedSignupConfigId: string
}

export class MetaSdkRequiresReloadError extends Error {}

export type MetaLoginResponse = {
  status?: 'connected' | 'not_authorized' | 'unknown'
  authResponse?: { code?: string } | null
}

export type MetaSdk = {
  init: (options: {
    appId: string
    version: string
    autoLogAppEvents: false
    xfbml: false
  }) => void
  login: (
    callback: (response: MetaLoginResponse) => void,
    options: {
      config_id: string
      response_type: 'code'
      override_default_response_type: true
      extras: {
        setup: Record<string, never>
        featureType?: 'whatsapp_business_app_onboarding'
        sessionInfoVersion?: '3'
      }
    },
  ) => void
}

const metaWindow: Window & {
  FB?: MetaSdk
  fbAsyncInit?: () => void
} = window
const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js'
let pendingLoad: Promise<MetaSdk> | undefined

function getReadySdk(): MetaSdk | undefined {
  const sdk = metaWindow.FB
  // The bootstrap queues login calls before it can open a popup synchronously.
  return sdk && !('__buffer' in sdk) ? sdk : undefined
}

export function loadMetaSdk(): Promise<MetaSdk> {
  const sdk = getReadySdk()
  if (sdk) return Promise.resolve(sdk)
  if (pendingLoad) return pendingLoad

  pendingLoad = new Promise<MetaSdk>((resolve, reject) => {
    const existingScript = Array.from(document.scripts).find(
      (script) => script.src.split(/[?#]/)[0] === SDK_URL,
    )
    const script = existingScript ?? document.createElement('script')
    const previousInit = metaWindow.fbAsyncInit
    const timer = window.setTimeout(fail, 15_000)

    function cleanUp(): void {
      window.clearTimeout(timer)
      script.removeEventListener('error', fail)
      if (metaWindow.fbAsyncInit === onReady) {
        metaWindow.fbAsyncInit = previousInit
      }
    }

    function fail(): void {
      cleanUp()
      if (metaWindow.FB && '__buffer' in metaWindow.FB) {
        reject(new MetaSdkRequiresReloadError('Could not load the Meta SDK.'))
        return
      }
      if (!metaWindow.FB) script.remove()
      reject(new Error('Could not load the Meta SDK.'))
    }

    function onReady(): void {
      try {
        previousInit?.()
      } finally {
        const loadedSdk = getReadySdk()
        if (loadedSdk) {
          cleanUp()
          resolve(loadedSdk)
        } else {
          fail()
        }
      }
    }

    metaWindow.fbAsyncInit = onReady
    script.addEventListener('error', fail)
    if (!existingScript) {
      script.async = true
      script.defer = true
      script.crossOrigin = 'anonymous'
      script.src = SDK_URL
      document.body.append(script)
    }
  })

  void pendingLoad.then(
    () => {
      pendingLoad = undefined
      return undefined
    },
    () => {
      pendingLoad = undefined
    },
  )
  return pendingLoad
}
